import { PaymentType } from '@/features/graphql/generated/graphql'
import { paymentReconciliationModes, paymentTypeOrder } from '@/features/shiftCloseouts/constants'
import {
    DailyShiftCloseout,
    ShiftCloseoutPaymentMethod,
    ShiftCloseoutPreviewData,
    ShiftCloseoutPreviewSource,
} from '@/features/shiftCloseouts/types'
import { calculateCashTransactionTotals } from '@/features/shiftCloseouts/utils/calculateCashTransactionTotals'
import { getPaymentType } from '@/features/shiftCloseouts/utils/getPaymentType'
import currencyJs from 'currency.js'
import invariant from 'tiny-invariant'

const getPaymentAmount = (
    paymentMethods: ShiftCloseoutPaymentMethod[],
    paymentType: PaymentType
): string =>
    paymentMethods.find((paymentMethod) => paymentMethod.paymentType === paymentType)
        ?.expectedAmount.amount ?? '0'

const transformPreview = (
    preview: ShiftCloseoutPreviewSource,
    paymentMethods: ShiftCloseoutPaymentMethod[]
): ShiftCloseoutPreviewData => {
    preview.paymentTotals.forEach(({ amount }) => {
        invariant(amount.currency === preview.currency, 'Currencies mismatch')
    })

    preview.cashTransactions.forEach(({ amount }) => {
        const value = Number(amount.amount)
        invariant(value >= 0 && !Object.is(value, -0), 'Cash transaction amount must be unsigned')
    })

    const cashTransactionTotal = calculateCashTransactionTotals(
        preview.currency,
        preview.cashTransactions
    ).total
    const cashHandover = currencyJs(getPaymentAmount(paymentMethods, PaymentType.Cash)).add(
        cashTransactionTotal.amount
    )
    const mealVoucherHandover = currencyJs(
        getPaymentAmount(paymentMethods, PaymentType.MealVoucher)
    )

    return {
        ...preview,
        handover: {
            cash: { amount: cashHandover.toString(), currency: preview.currency },
            mealVouchers: {
                amount: mealVoucherHandover.toString(),
                currency: preview.currency,
            },
            total: {
                amount: cashHandover.add(mealVoucherHandover).toString(),
                currency: preview.currency,
            },
        },
    }
}

export const transformShiftCloseoutPreview = (
    preview: ShiftCloseoutPreviewSource,
    closedAt: string
): DailyShiftCloseout => {
    const paymentMethods = preview.paymentTotals
        .map<ShiftCloseoutPaymentMethod>(({ amount, paymentType: identifier }) => {
            const paymentType = getPaymentType(identifier)
            const mode = paymentType ? paymentReconciliationModes[paymentType] : 'automatic'

            return { expectedAmount: amount, identifier, mode, paymentType }
        })
        .sort(
            (first, second) =>
                (first.paymentType
                    ? paymentTypeOrder[first.paymentType]
                    : Number.MAX_SAFE_INTEGER) -
                (second.paymentType
                    ? paymentTypeOrder[second.paymentType]
                    : Number.MAX_SAFE_INTEGER)
        )

    return {
        closedAt,
        paymentMethods,
        preview: transformPreview(preview, paymentMethods),
    }
}
